import { useEffect, useRef, useState } from "react";
import Auth from "../../Auth";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { CASTLE_DEVICE_LIMIT_RULE } from "~shared/castleTrialDeviceLimit";
import type { CastleTrialDeviceLimit } from "~shared/castleTrialDeviceLimit";

function dateValue(value: any): Date | null {
  if (!value) return null;
  const date = value.toDate?.() || (typeof value._seconds === "number" ? new Date(value._seconds * 1000) : new Date(value));
  return Number.isFinite(+date) ? date : null;
}

export function TrialAIReview({ userId, subscription, toolsEnabled, onUpdated }: {
  userId: string;
  subscription?: { data?: {
    trialAIReviewExempt?: boolean;
    trialAIReviewExemptUntil?: unknown;
    trialAIDeviceLimit?: Omit<CastleTrialDeviceLimit, "evaluatedAt"> & { evaluatedAt?: unknown };
  } };
  toolsEnabled: boolean;
  onUpdated: () => Promise<void>;
}) {
  const [override, setOverride] = useState<{ userId: string; exempt: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState("");
  const until = dateValue(subscription?.data?.trialAIReviewExemptUntil);
  const savedExempt = subscription?.data?.trialAIReviewExempt === true;
  const effectiveOverride = override?.userId === userId ? override : null;
  const exempt = effectiveOverride ? effectiveOverride.exempt : savedExempt || !!(until && until > new Date());
  const temporary = !effectiveOverride && !savedExempt && exempt && until;
  const assessment = subscription?.data?.trialAIDeviceLimit;
  const evaluatedAt = dateValue(assessment?.evaluatedAt);
  useEffect(() => { setOverride(null); }, [userId, subscription?.data]);

  async function update() {
    if (!toolsEnabled || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    let saved = false;
    try {
      const result = await Auth.fetchWithIdToken(`/api/admin/user/${encodeURIComponent(userId)}/trial-ai-review`, {
        action: exempt ? "restore" : "clear",
      });
      if (!result?.success) throw new Error(result?.message || "Unable to update trial AI review.");
      saved = true;
      setOverride({ userId, exempt: result.exempt });
      await onUpdated();
    } catch (err: any) {
      setError(saved ? "The change was saved, but account details could not refresh. Reload this page to refresh them."
        : err?.message || "Unable to update trial AI review.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
      <h4 className="text-base font-semibold text-purple-900 mb-2">Trial AI restriction</h4>
      <p className="text-sm text-purple-700 mb-3">
        Clear this account’s trial AI restriction until an admin restores checks.
        Credit limits and other security checks still apply.
      </p>
      <p className="text-sm text-purple-700 mb-3">
        {assessment?.ruleVersion === CASTLE_DEVICE_LIMIT_RULE
          ? `Castle reported ${assessment.usersPerDevice} users per device; disposable-email denial: ${assessment.disposableEmailDenied ? "yes" : "no"}. ${assessment.restricted ? "Meets" : "Does not meet"} the combined ${assessment.threshold}-account restriction.${evaluatedAt ? ` Evaluated ${evaluatedAt.toLocaleString()}.` : ""}`
          : "No device-account assessment recorded."}
      </p>
      <p role="status" className="text-sm text-purple-900 mb-3">
        {temporary ? `Temporarily cleared until ${temporary.toLocaleString()}`
          : exempt ? "Cleared by admin — trial restriction and disposable-email denial are bypassed."
            : "Trial AI checks are enabled."}
      </p>
      <SudoButton type="button" variant="secondary" disabled={!toolsEnabled || busy} onClick={() => void update()}>
        {busy ? "Saving…" : exempt ? "Restore trial AI checks" : "Clear trial AI restriction"}
      </SudoButton>
      {error && <p role="alert" className="text-sm text-red-700 mt-3">{error}</p>}
    </div>
  );
}
